import styled, { css } from 'styled-components';
import { Box, Button, Divider, Heading, Stack, SubtleText } from '@99designs/emu';
import format from '@99designs/ninedown/dist/Format';
import sanitize from '@99designs/ninedown/dist/Sanitize';
import { linkTo } from '@99designs/routing';
import { useTrackLink } from '@99designs/tracking';
import { trackLink } from '../../lib/tracking';
import {
    CollaborationMethod as CollaborationMethodValue,
    FixedPriceProjectProduct,
    ProductGroup,
    RelatedProduct,
} from '../../types';
import { AdditionalCost } from '../AdditionalCost';
import { BaseLayout } from '../BaseLayout';
import { CheckoutSummary } from '../CheckoutSummary';
import { CollaborationMethod, collaborationMethods } from '../CollaborationMethod';
import { Hero } from '../Hero';
import { HowItWorks, howItWorksContent } from '../HowItWorks';
import { PackageInclusions } from '../PackageInclusions';
import { ProductGroupSummary } from '../ProductGroupSummary';
import type { ZendeskLanguage } from '@99designs/routing';
import type { InspirationProps } from '../BaseLayout/BaseLayout';

const InclusionsWrapper = styled(Box)<{ lastItem: boolean }>`
    ${({ lastItem }) =>
        lastItem &&
        css`
            padding-bottom: 0;
        `}
`;

export type Props = {
    productGroup: ProductGroup;
    products: FixedPriceProjectProduct[];
    inspiration?: InspirationProps;
    relatedProducts?: RelatedProduct[];
    locale: string;
    showAltCta?: boolean;
    altCtaText?: string;
};

export function FixedPriceProjectLayout({
    productGroup,
    products,
    inspiration,
    relatedProducts,
    locale,
    showAltCta,
    altCtaText,
}: Props) {
    const lang = locale.split('-')[0] as ZendeskLanguage;
    const product = products[0];
    const ctaURL = linkTo.briefCreate(product.name);

    const trackCTAClick = (event: React.MouseEvent) =>
        trackLink(event, 'Continue CTA on Product details page clicked', {
            category: 'Product Page',
            label: 'Content:Main CTA',
            eventDetail: `/design/${product.name};/design/briefs/create/${product.key};Main CTA;Continue to brief`,
            navigationDetail: 'Continue to brief',
            pageName: `${product.name}:Product Page`,
            pageStage: 'Discover',
            pageSection: 'Product Page',
            pageDept: 'Design Services',
        });

    const secondaryCTARef = useTrackLink(
        {
            event: 'Secondary CTA on Product details page clicked',
            meta: {},
        },
        []
    );

    return (
        <BaseLayout
            relatedProducts={relatedProducts}
            inspiration={inspiration}
            faqs={productGroup.attributes.faqs}
            productNameForTracking={product.name}
            productIdForTracking={product.name} // FIXME : Replace productIdForTracking
            leftColumn={
                <>
                    <Hero
                        images={productGroup.attributes.hero}
                        type={productGroup.attributes.type}
                    />
                    <Box
                        display={['none', 'block', 'block', 'block']}
                        paddingTop={['none', '1.5bu', '1.5bu', '1.5bu']}
                    >
                        <HowItWorks
                            {...howItWorksContent(productGroup.attributes.collaborationMethod)}
                        />
                    </Box>
                </>
            }
            rightColumn={
                <>
                    <Stack space="2bu">
                        <ProductGroupSummary
                            title={product.title}
                            price={product.pricing}
                            subtitle={productGroup.attributes.subtitle}
                            description={product.description}
                            type={productGroup.attributes.type}
                        />

                        {productGroup.attributes.collaborationMethod ===
                            CollaborationMethodValue.OneToOneProject && (
                            <Button
                                key={`${product.name}-primary-cta`}
                                href={ctaURL}
                                onClick={trackCTAClick}
                                width="fill"
                                size="medium"
                                disabled={!product.pricing}
                            >
                                {__('Continue to brief')}
                            </Button>
                        )}

                        <Divider />

                        <PackageInclusions
                            inclusions={product.details.map((detail, i) => {
                                const isLastItem = i + 1 === product.details.length;
                                return (
                                    <InclusionsWrapper
                                        paddingBottom={['1bu', '1bu', '1.5bu', '1.5bu']}
                                        lastItem={isLastItem}
                                    >
                                        <Stack space="0.25bu">
                                            <Heading
                                                appearanceLevel={5}
                                                accessibilityLevel={7}
                                                as="p"
                                            >
                                                {detail.title}
                                            </Heading>
                                            <Box
                                                dangerouslySetInnerHTML={{
                                                    __html: format(sanitize(detail.description)),
                                                }}
                                            />
                                        </Stack>
                                    </InclusionsWrapper>
                                );
                            })}
                        />
                    </Stack>

                    <CollaborationMethod
                        type={productGroup.attributes.collaborationMethod}
                        productName={product.name}
                        {...collaborationMethods(productGroup.attributes.collaborationMethod, lang)}
                    />

                    {product.additionalDetails.length > 0 && (
                        <AdditionalCost items={product.additionalDetails} />
                    )}

                    {product.disclaimer && (
                        <SubtleText>
                            {__('Disclaimer')}: {product.disclaimer}
                        </SubtleText>
                    )}

                    <CheckoutSummary
                        pricing={product.pricing}
                        cta={{
                            key: product.name,
                            onClick: trackCTAClick,
                            href: ctaURL,
                        }}
                        secondaryCta={
                            product.secondaryCTALink
                                ? {
                                      key: `${product.name}-secondary-cta`,
                                      ref: secondaryCTARef,
                                      href: product.secondaryCTALink,
                                  }
                                : undefined
                        }
                        showAltCta={showAltCta}
                        altCtaText={altCtaText}
                    />
                </>
            }
        />
    );
}
